import { useState } from "react";
import { useToast } from "../../contexts/ToastContext";
import { useChangePasswordMutation } from "../../store/api/authApi";
import Icon from "../AppIcon";
import Button from "../ui/Button";

interface PasswordChangeFormProps {
	onSuccess: () => void;
}

const PasswordChangeForm: React.FC<PasswordChangeFormProps> = ({
	onSuccess,
}) => {
	const [changePassword, { isLoading }] = useChangePasswordMutation();
	const { success, error: showError } = useToast();
	const [formData, setFormData] = useState({
		oldPassword: "",
		newPassword: "",
	});
	const [errors, setErrors] = useState<Record<string, string>>({});
	const [showPasswords, setShowPasswords] = useState({
		old: false,
		new: false,
	});

	const handleInputChange = (field: string, value: string) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
		if (errors[field]) {
			setErrors((prev) => ({ ...prev, [field]: "" }));
		}
	};

	const validate = (): boolean => {
		const newErrors: Record<string, string> = {};

		if (!formData.oldPassword) {
			newErrors.oldPassword = "Current password is required";
		}

		if (!formData.newPassword) {
			newErrors.newPassword = "New password is required";
		} else if (formData.newPassword.length < 8) {
			newErrors.newPassword = "Password must be at least 8 characters long";
		}

		if (formData.oldPassword === formData.newPassword) {
			newErrors.newPassword =
				"New password must be different from current password";
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!validate()) {
			return;
		}

		setErrors({});

		try {
			await changePassword({
				old_password: formData.oldPassword,
				new_password: formData.newPassword,
			}).unwrap();

			// Reset form
			setFormData({
				oldPassword: "",
				newPassword: "",
			});
			setErrors({});

			success("Password changed successfully!");
			onSuccess();
		} catch (error: any) {
			console.error("Password change error:", error);

			if (error?.data?.error) {
				const errorMessage =
					typeof error.data.error === "string"
						? error.data.error
						: error.data.error[0] || "Failed to change password";
				showError(errorMessage);
				setErrors({ submit: errorMessage });
			} else if (error?.data?.old_password) {
				const errorMessage = Array.isArray(error.data.old_password)
					? error.data.old_password[0]
					: error.data.old_password;
				setErrors({ oldPassword: errorMessage });
				showError(errorMessage);
			} else if (error?.data?.new_password) {
				const errorMessage = Array.isArray(error.data.new_password)
					? error.data.new_password[0]
					: error.data.new_password;
				setErrors({ newPassword: errorMessage });
				showError(errorMessage);
			} else {
				const errorMessage =
					error?.data?.message ||
					"Failed to change password. Please try again.";
				setErrors({ submit: errorMessage });
				showError(errorMessage);
			}
		}
	};

	return (
		<form
			onSubmit={handleSubmit}
			className="space-y-4 bg-muted/30 p-4 rounded-lg"
		>
			<h3 className="text-lg font-semibold text-foreground mb-4">
				Change Password
			</h3>

			<div className="space-y-4">
				{/* Current Password */}
				<div className="space-y-2">
					<label className="text-sm font-medium text-foreground">
						Current Password
					</label>
					<div className="relative">
						<input
							type={showPasswords.old ? "text" : "password"}
							value={formData.oldPassword}
							onChange={(e) => handleInputChange("oldPassword", e.target.value)}
							placeholder="Enter your current password"
							required
							className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 pr-10 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
						/>
						<button
							type="button"
							onClick={() =>
								setShowPasswords((prev) => ({ ...prev, old: !prev.old }))
							}
							className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
							aria-label={showPasswords.old ? "Hide password" : "Show password"}
						>
							<Icon name={showPasswords.old ? "EyeOff" : "Eye"} size={18} />
						</button>
					</div>
					{errors.oldPassword && (
						<p className="text-sm text-destructive">{errors.oldPassword}</p>
					)}
				</div>

				{/* New Password */}
				<div className="space-y-2">
					<label className="text-sm font-medium text-foreground">
						New Password
					</label>
					<div className="relative">
						<input
							type={showPasswords.new ? "text" : "password"}
							value={formData.newPassword}
							onChange={(e) => handleInputChange("newPassword", e.target.value)}
							placeholder="Enter your new password"
							required
							className={`flex h-10 w-full rounded-md border bg-background px-3 py-2 pr-10 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
								errors.newPassword
									? "border-destructive focus-visible:ring-destructive"
									: "border-input"
							}`}
						/>
						<button
							type="button"
							onClick={() =>
								setShowPasswords((prev) => ({ ...prev, new: !prev.new }))
							}
							className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
							aria-label={showPasswords.new ? "Hide password" : "Show password"}
						>
							<Icon name={showPasswords.new ? "EyeOff" : "Eye"} size={18} />
						</button>
					</div>
					{errors.newPassword && (
						<p className="text-sm text-destructive">{errors.newPassword}</p>
					)}
					{formData.newPassword &&
						formData.newPassword.length < 8 &&
						!errors.newPassword && (
							<p className="text-xs text-muted-foreground">
								Password must be at least 8 characters long
							</p>
						)}
				</div>
			</div>

			{errors.submit && (
				<div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
					{errors.submit}
				</div>
			)}

			<div className="flex items-center justify-end space-x-3 pt-4">
				<Button type="submit" disabled={isLoading}>
					{isLoading ? (
						<>
							<Icon name="Loader2" size={16} className="mr-2 animate-spin" />
							Changing Password...
						</>
					) : (
						<>
							<Icon name="Key" size={16} className="mr-2" />
							Change Password
						</>
					)}
				</Button>
			</div>
		</form>
	);
};

export default PasswordChangeForm;
